import {
  CoverageNeed,
  NeedName,
  ProductCoverageName,
  ProductName,
} from "Store/akaAdvice/interfaces/CollectiveAdvice";

import Popover from "@storeblocks/popover";
import { StyledAlert, StyledRow, WrapLgMarginBottom } from "App/global-style";
import { AdviceProductDetailsFormValues } from "Components/Features/CheckPrice/Products/Advice/ProductDetails/FormElements/interfaces";
import {
  getOneTimePaymentCheckboxText,
  replaceUserSupportNumberInText,
} from "Components/Features/CheckPrice/Products/Advice/ProductPrice/AdviceCards/utils";
import { useAdviceDetailsFormValues } from "Components/Features/CheckPrice/Products/Advice/ProductPrice/hooks/stateSelectors";
import { Alert } from "Components/Form/Alert";
import { Checkbox } from "Components/Form/Checkbox";
import { checkShouldPurchaseFromAdvisor } from "Features/CheckPrice/Products/Advice/ProductPriceAka/utils";
import { UserAuthorizationLevel } from "Store/advice/interfaces";
import { useIsAdvisorSelector } from "Store/advisor/advisorSelectors";
import {
  getSelectedCoverageValue,
  isAllowed,
  isRecommended,
} from "Store/akaAdvice/interfaces/getters";
import { getCorrectAkaAdviceValues } from "Store/akaAdvice/mappers/mapToFormValues/utils";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { usePartnerId } from "Store/partners/partnerHooks";
import { TextLanguage } from "Store/texts/interfaces";
import { FormikProps } from "formik";
import React from "react";
import { displayCanGetHigherFromAdvisor } from "../../../../utils";
import { InsurancePrice } from "../../../AdvicePrice/InsurancePrice";
import { CloseToMaxAgeWarning } from "../../../CloseToMaxAgeWarning";
import { AkaAdvicePriceFormValues } from "../../../interfaces";
import { CardSubHeader, StyledCheckbox } from "../../../styled";
import {
  DISABILITY_CAPITAL_END_AGE,
  DISABILITY_CAPITAL_MAX_AGE,
} from "../../disabilityAdviceCardConstants";
import { DisabilityOneTimePaymentCustomerOptions } from "./DisabilityOneTimePaymentCustomerOptions";
import { PurchaseFromAdvisor } from "./PurchaseFromAdvisor";
import { PurchaseFromAdvisorInsuredSumOnly } from "./PurchaseFromAdvisorInsuredSumOnly";
import { PurchaseFromAdvisorOneTimePaymentOnly } from "./PurchaseFromAdvisorOneTimePaymentOnly";
import { PriceProps } from "./disabilityOneTimePaymentTypes";

type Props = PriceProps & {
  formProps: FormikProps<AkaAdvicePriceFormValues>;
  need: CoverageNeed;
  recalculatePrice: (
    formValues: AkaAdvicePriceFormValues,
    specificProduct: ProductName,
  ) => void;
  adviceCard: any;
  lang: TextLanguage;
};

export const DisabilityOneTimePaymentCustomerForm = ({
  formProps,
  need,
  recalculatePrice,
  adviceCard,
  lang,
  ...priceProps
}: Props): React.ReactElement | null => {
  const coverageName = ProductCoverageName.DISABILITY_FLEX_CAPITAL;
  const isAdvisor = useIsAdvisorSelector();
  const adviceDetails =
    useAdviceDetailsFormValues() as AdviceProductDetailsFormValues;
  const partnerId = usePartnerId();

  const maxAdvisorAdviceValue = getCorrectAkaAdviceValues(
    need,
    ProductCoverageName.DISABILITY_FLEX_PENSION,
    isAdvisor,
    UserAuthorizationLevel.PRODUCT_MANAGER,
  ).maxAdviceValue;
  const shouldPurchasePensionFromAdvisor = checkShouldPurchaseFromAdvisor(
    isAdvisor,
    need,
    ProductCoverageName.DISABILITY_FLEX_PENSION,
  );

  const maxAdvisorAdviceOtpValue = getCorrectAkaAdviceValues(
    need,
    coverageName,
    isAdvisor,
    UserAuthorizationLevel.PRODUCT_MANAGER,
  ).maxAdviceValue;
  const shouldPurchaseCapitalFromAdvisor = checkShouldPurchaseFromAdvisor(
    isAdvisor,
    need,
    coverageName,
  );
  const isCapitalRecommended = isRecommended(
    need,
    ProductCoverageName.DISABILITY_FLEX_CAPITAL,
  );

  return (
    <>
      {shouldPurchasePensionFromAdvisor &&
        !shouldPurchaseCapitalFromAdvisor && (
          <PurchaseFromAdvisorInsuredSumOnly
            need={need}
            purchaseFromAdvisor={
              adviceCard.adviceUnavailableMessages.purchaseFromAdvisor
            }
            lang={lang}
            maxAdvisorAdviceValue={maxAdvisorAdviceValue}
            maxAdvisorAdviceOtpValue={maxAdvisorAdviceOtpValue}
          />
        )}
      {isAllowed(need, coverageName) && (
        <WrapLgMarginBottom>
          <CardSubHeader fw={700} size="16px">
            Engangsutbetaling
            {isCapitalRecommended && (
              <Popover title="Engangsutbetaling">
                {adviceCard.disabilityOneTimePayment.headerPopover[lang]}
              </Popover>
            )}
          </CardSubHeader>
          {!isCapitalRecommended && (
            <p>{adviceCard.disabilityOneTimePayment.adviceText.hint[lang]}</p>
          )}
          <StyledRow className="AkaAdviceInsuranceForm">
            <div>
              <StyledCheckbox>
                <Checkbox
                  id="disabilityOneTimePaymentCheckbox"
                  name="disabilityOneTimePaymentCheckbox"
                  label={getOneTimePaymentCheckboxText(
                    need,
                    adviceDetails,
                    adviceCard.disabilityOneTimePayment,
                    lang,
                  )}
                  onChange={(e) => {
                    const selected = e.target.checked;
                    recalculatePrice(
                      {
                        ...formProps.values,
                        disabilityOneTimePaymentCheckbox: selected,
                      },
                      INSURANCE_NAME_VALUE.DISABILITY_FLEX,
                    );
                  }}
                />
              </StyledCheckbox>
              <DisabilityOneTimePaymentCustomerOptions
                formProps={formProps}
                need={need}
                recalculatePrice={recalculatePrice}
                adviceCard={adviceCard}
                lang={lang}
              />
              {formProps.values.disabilityOneTimePaymentChangedWarning && (
                <Alert
                  title={
                    adviceCard.warnings[
                      ProductCoverageName.DISABILITY_FLEX_CAPITAL
                    ].chosenNotAvailable.title[lang]
                  }
                  description={
                    adviceCard.warnings[
                      ProductCoverageName.DISABILITY_FLEX_CAPITAL
                    ].chosenNotAvailable.description[lang]
                  }
                  type="warning"
                  marginTop="32px"
                  style={{ display: "flex" }} // TODO: Maybe do this in main component? Why is inline default?
                />
              )}
              {displayCanGetHigherFromAdvisor(
                getSelectedCoverageValue(
                  ProductCoverageName.DISABILITY_FLEX_CAPITAL,
                  formProps.values,
                  isAdvisor,
                ),
                need,
                ProductCoverageName.DISABILITY_FLEX_CAPITAL,
                isAdvisor,
              ) && (
                <StyledAlert
                  title={replaceUserSupportNumberInText(
                    partnerId,
                    adviceCard.adviceUnavailableMessages.purchaseFromAdvisor
                      .userSupportNumber,
                    adviceCard.adviceUnavailableMessages.purchaseFromAdvisor
                      .maxPayoutCustomer[lang],
                    lang,
                  )}
                  marginBottom="0px"
                />
              )}
              {shouldPurchaseCapitalFromAdvisor &&
                !shouldPurchasePensionFromAdvisor && (
                  <PurchaseFromAdvisorOneTimePaymentOnly
                    need={need}
                    purchaseFromAdvisor={
                      adviceCard.adviceUnavailableMessages.purchaseFromAdvisor
                    }
                    lang={lang}
                    maxAdvisorAdviceValue={maxAdvisorAdviceValue}
                    maxAdvisorAdviceOtpValue={maxAdvisorAdviceOtpValue}
                    marginBottom="0px"
                  />
                )}
              {shouldPurchasePensionFromAdvisor &&
                shouldPurchaseCapitalFromAdvisor && (
                  <PurchaseFromAdvisor
                    need={need}
                    purchaseFromAdvisor={
                      adviceCard.adviceUnavailableMessages.purchaseFromAdvisor
                    }
                    lang={lang}
                    maxAdvisorAdviceValue={maxAdvisorAdviceValue}
                    maxAdvisorAdviceOtpValue={maxAdvisorAdviceOtpValue}
                    marginBottom="0px"
                  />
                )}
            </div>
            {priceProps.displayPrice && (
              <InsurancePrice
                needName={NeedName.DISABILITY}
                productCoverages={[coverageName]}
                priceIsChanging={priceProps.priceIsChanging}
                priceIsLoading={priceProps.priceIsLoading}
              />
            )}
          </StyledRow>
          <CloseToMaxAgeWarning
            need={need}
            coverageName={coverageName}
            warningTexts={adviceCard.warnings}
            lang={lang}
            warningVariables={{
              maxAge: DISABILITY_CAPITAL_MAX_AGE,
              endAge: DISABILITY_CAPITAL_END_AGE,
            }}
          />
        </WrapLgMarginBottom>
      )}
    </>
  );
};
